import Link from 'next/link';
import cn from 'classnames';
import { useQuery } from '@apollo/client';
// Api
import { GET_MY_TICKETS } from 'api/ticketEvents/queries';
// Types
import { TicketStatus } from 'api/graphql-global-types';
import {
  GetMyTickets,
  GetMyTicketsVariables,
} from 'api/ticketEvents/types/GetMyTickets';
// Constants
import { MY_TICKETS } from 'components/Purchase/purchaseTabs';
// Helpers
import {
  MY_HELD_TICKETS_INPUT,
  formatHeldTicketCount,
} from 'helpers/myTickets';
// Hooks
import { useAppContext } from 'hooks';
// Styles
import styles from './HeldTicketsLink.module.scss';

type HeldTicketsLinkProps = {
  ticketEventId: string;
  className?: string;
};

/** Tells a signed-in fan they already hold tickets for this event. */
const HeldTicketsLink = ({
  ticketEventId,
  className,
}: HeldTicketsLinkProps): JSX.Element | null => {
  const { token } = useAppContext();
  const { data } = useQuery<GetMyTickets, GetMyTicketsVariables>(
    GET_MY_TICKETS,
    {
      variables: { input: MY_HELD_TICKETS_INPUT },
      skip: !token,
      fetchPolicy: 'cache-and-network',
    }
  );

  const count =
    data?.myTickets.entities.filter(
      ({ ticketEvent, status }) =>
        ticketEvent.id === ticketEventId && status !== TicketStatus.Revoked
    ).length ?? 0;

  if (!token || count === 0) {
    return null;
  }

  return (
    <p className={cn(styles.root, className)}>
      {formatHeldTicketCount(count)}
      <span aria-hidden="true">·</span>
      <Link href={MY_TICKETS}>
        <a className={styles.link}>View</a>
      </Link>
    </p>
  );
};

export default HeldTicketsLink;
